import { CreditCard, LockOpen, Ticket, Zap } from "lucide-react";
import CanonicalTags from "@/components/seo/CanonicalTags";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { Accordion, CheckList, CtaBand, SignUpCta, WaitlistCta } from "@/components/marketing/parts";

type Plan = {
  audience: string;
  name: string;
  featured?: boolean;
  /** Founding rate after beta, with the regular rate. Gold rule. */
  founding?: string;
  /** Agents' free plan note in place of a founding rate. */
  note?: string;
  summary: string;
  /** Heading over the feature list ("Available during beta" for organization plans). */
  featuresLabel?: string;
  features: string[];
  /** Plain note under the feature list. */
  footnote?: string;
  cta: React.ReactNode;
};

// Organization plans: what an organization can use today, during beta. These
// are current beta capabilities shared by every organization account, not
// paid-tier entitlements; paid-plan features are confirmed before billing.
const AVAILABLE_DURING_BETA = "Available during beta";

const PLANS: Plan[] = [
  {
    audience: "For Agents",
    name: "Free",
    note: "Free forever",
    summary: "Free forever. No credit card required.",
    features: [
      "Explore teams & brokerages anonymously",
      "Compare teams and brokerages side by side",
      "Fit Score",
      "Updates from teams and brokerages",
      "Direct messaging with teams & brokerages",
    ],
    cta: (
      <SignUpCta variant="outline" className="mk-btn--block" style={{ marginTop: "auto" }}>
        Get Started Free
      </SignUpCta>
    ),
  },
  {
    audience: "For Teams & Brokerages",
    name: "Professional",
    featured: true,
    founding: "Founding rate after beta: $349/mo (regular $499/mo)",
    summary: "Help agents understand what your team or brokerage offers.",
    featuresLabel: AVAILABLE_DURING_BETA,
    features: [
      "Team or brokerage profile with photos, videos and your story",
      "Profile views and engagement summaries",
      "Fit Score matching with agents",
      "Two-way messaging with interested agents",
      "Meeting booking through your calendar link",
    ],
    cta: (
      <WaitlistCta className="mk-btn--block" style={{ marginTop: "auto" }}>
        Get Early Access
      </WaitlistCta>
    ),
  },
  {
    audience: "For Teams & Brokerages",
    name: "Enterprise",
    founding: "Founding rate after beta: $699/mo (regular $999/mo)",
    summary: "For larger teams and brokerages.",
    featuresLabel: AVAILABLE_DURING_BETA,
    features: [
      "Team or brokerage profile and your story",
      "Culture, training, and support showcase",
      "Profile views and engagement summaries",
      "Messaging with interested agents",
      "Meeting booking through your calendar link",
    ],
    footnote:
      "Enterprise is a planned post-beta option. Paid-plan features will be confirmed before billing begins. Contact us to discuss your organization's needs.",
    cta: (
      <a href="mailto:jesse@compaire.co" className="mk-btn mk-btn--dark mk-btn--block" style={{ marginTop: "auto" }}>
        Contact Us
      </a>
    ),
  },
];

const BENEFITS = [
  {
    Icon: LockOpen,
    title: "Free during beta",
    body: "Join beta at no cost. No credit card required.",
  },
  {
    Icon: CreditCard,
    title: "No credit card",
    body: "We don't collect payment information during beta. Billing only begins if you opt in after beta ends.",
  },
  {
    Icon: Ticket,
    title: "Founding-member rates",
    body: "Keep your founding rate for as long as you remain subscribed: Pro $349/mo (vs. $499), Enterprise $699/mo (vs. $999). Not available after cancelling and resubscribing.",
  },
];

const FAQS = [
  {
    q: "Is Compaire really free for agents?",
    a: "Yes. Agents always explore, compare, and message teams and brokerages for free — no credit card, no trial period. Agent plans stay free after beta too. Teams and brokerages can choose optional paid plans after beta.",
  },
  {
    q: "What does 'beta pricing' actually mean?",
    a: "You can join Compaire's beta at no cost, with no credit card required. Paid organization plans are planned for after beta, and billing begins only if you opt in. Agents stay free.",
  },
  {
    q: "When does beta end?",
    a: "We'll announce the beta exit date at least 30 days in advance. Your account stays free until then, and you'll have plenty of time to decide how you want to continue.",
  },
  {
    q: "What happens to my plan when beta ends?",
    a: "Nothing auto-converts to paid without your opt-in. When beta ends, organizations choose one of three paths: upgrade to Professional or Enterprise at founding-member pricing (locked in for as long as you stay subscribed — $349/mo Pro, $699/mo Enterprise), continue on the ongoing free option with a team or brokerage profile on Compaire, or close your account. Agent plans aren't affected — agents stay free.",
  },
  {
    q: "Is there a long-term contract?",
    a: "No. Professional and Enterprise are planned as monthly plans with no long-term commitment.",
  },
  {
    q: "Can I cancel at any time?",
    a: "Yes. There's nothing to cancel during beta, since nothing is charged. After beta, paid plans are planned as month-to-month, so you'll be able to cancel at any time, and your profile will stay active through the end of the billing period. Note: if you cancel and rejoin later, founding-member pricing is no longer available — you'd subscribe at the then-current regular price.",
  },
  {
    q: "Do you offer discounts for multi-location or franchise organizations?",
    a: (
      <>
        Enterprise is a planned post-beta option for large brokerages, multi-location organizations and franchise
        systems. Its pricing for those structures isn't set yet. Reach out at{" "}
        <a href="mailto:sales@compaire.co">sales@compaire.co</a> to discuss your organization.
      </>
    ),
  },
];

// Every card is the same seven rows: audience, name, price, summary, features,
// footnote, CTA. The cards share those rows through CSS subgrid, so side by
// side each row takes the tallest card's height: the price section's divider
// lines up even where one founding-rate note wraps to two lines, and the CTAs
// stay level. Stacked on a phone, each card owns its rows and sizes naturally.
const PLAN_CARD_ROWS = 7;

const PlanCard = ({ plan }: { plan: Plan }) => (
  <div
    style={{
      display: "grid",
      gridRow: `span ${PLAN_CARD_ROWS}`,
      gridTemplateRows: "subgrid",
      rowGap: 22,
      background: "#fff",
      border: plan.featured ? "2px solid var(--mk-primary)" : "1px solid var(--mk-border)",
      borderRadius: 8,
      // The featured card's 2px border takes exactly 1px from its padding at
      // every width, so its contents sit on the same lines as its neighbours'.
      padding: plan.featured ? "calc(clamp(28px,3cqi,36px) - 1px)" : "clamp(28px,3cqi,36px)",
      boxShadow: plan.featured ? "0 24px 48px -28px rgba(19,86,90,0.35)" : undefined,
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 8,
        minHeight: 26,
        flexWrap: "wrap",
      }}
    >
      <span className="mk-label mk-label--muted">{plan.audience}</span>
    </div>
    <h2 style={{ font: "600 var(--mk-text-h2-sm)/1.15 var(--mk-sans)" }}>{plan.name}</h2>
    <div className="mk-stack" style={{ gap: 8, paddingBottom: 22, borderBottom: "1px solid var(--mk-border)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
        <span style={{ font: "700 var(--mk-text-stat-lg)/1 var(--mk-sans)" }}>$0</span>
        <span style={{ font: "500 var(--mk-text-label)/1.3 var(--mk-sans)", color: "var(--mk-muted)" }}>/mo</span>
      </div>
      <span style={{ font: "400 var(--mk-text-small)/1.4 var(--mk-sans)", color: "var(--mk-muted)" }}>Beta · No credit card</span>
      {plan.note && (
        <span style={{ font: "600 var(--mk-text-small)/1.4 var(--mk-sans)", color: "var(--mk-primary)" }}>{plan.note}</span>
      )}
      {plan.founding && (
        <span
          style={{
            font: "500 var(--mk-text-small)/1.4 var(--mk-sans)",
            borderLeft: "2px solid var(--mk-gold)",
            paddingLeft: 10,
          }}
        >
          {plan.founding}
        </span>
      )}
    </div>
    <p style={{ font: "400 var(--mk-text-body)/1.6 var(--mk-sans)", color: "var(--mk-ink-2)" }}>{plan.summary}</p>
    <div className="mk-stack" style={{ gap: 14 }}>
      {plan.featuresLabel && (
        <h3 style={{ font: "600 var(--mk-text-h4)/1.2 var(--mk-sans)", color: "var(--mk-primary)" }}>{plan.featuresLabel}</h3>
      )}
      <CheckList
        items={plan.features}
        size={18}
        className="mk-checklist--top"
        style={{ gap: 12, font: "400 var(--mk-text-body)/1.6 var(--mk-sans)" }}
      />
    </div>
    {plan.footnote ? (
      <p style={{ font: "400 var(--mk-text-small)/1.4 var(--mk-sans)", color: "var(--mk-muted)", textWrap: "pretty" }}>
        {plan.footnote}
      </p>
    ) : (
      <span aria-hidden="true" />
    )}
    <div style={{ alignSelf: "end" }}>{plan.cta}</div>
  </div>
);

const PricingPage = () => (
  <MarketingLayout active="pricing">
    <CanonicalTags
      path="/pricing"
      title="Compaire Pricing | Free Options & Founding Offers"
      description="Agents stay free. Teams and brokerages join beta at no cost, with free profiles and optional paid plans afterward. See founding-member offers."
    />

    <section style={{ padding: "clamp(64px,9cqi,128px) 0 clamp(48px,6cqi,72px)" }}>
      <div className="mk-container mk-stack" style={{ alignItems: "center", textAlign: "center", gap: 28 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            font: "500 var(--mk-text-small)/1.4 var(--mk-sans)",
            color: "var(--mk-ink-2)",
          }}
        >
          <Zap size={16} color="var(--mk-primary)" aria-hidden="true" />
          Beta · Free to join · Founding-member rates locked
        </span>
        <h1 className="mk-h1" style={{ maxWidth: 960 }}>
          Free during beta. <em className="mk-em">Founding discounts forever.</em>
        </h1>
        <p style={{ font: "600 var(--mk-text-lede)/1.55 var(--mk-sans)", textWrap: "pretty" }}>
          Keep your founding rate for as long as you remain subscribed.
        </p>
        <p className="mk-lede" style={{ maxWidth: 680 }}>
          Agents are free forever. Teams &amp; brokerages join free during beta and can opt in to founding-member
          pricing when paid billing starts. Founding pricing isn't available after cancelling and resubscribing.
        </p>
      </div>
    </section>

    <section style={{ padding: "0 0 clamp(56px,7cqi,96px)" }}>
      <div
        className="mk-container"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,320px),1fr))",
          gap: 16,
          alignItems: "stretch",
        }}
      >
        {PLANS.map((plan) => (
          <PlanCard key={plan.name} plan={plan} />
        ))}
      </div>
      <div className="mk-container">
        <p
          style={{
            marginTop: 16,
            padding: "16px 20px",
            background: "var(--mk-tint)",
            borderRadius: 8,
            font: "400 var(--mk-text-body)/1.6 var(--mk-sans)",
            textWrap: "pretty",
          }}
        >
          Beta access is free. Organization accounts use the same available beta features. Professional and
          Enterprise pricing applies after beta; paid-plan features will be confirmed before billing begins.
        </p>
      </div>
    </section>

    <section className="mk-section--white mk-section--rule" style={{ padding: "clamp(72px,9cqi,112px) 0" }}>
      <div className="mk-container mk-stack" style={{ gap: 40 }}>
        <h2 style={{ font: "600 var(--mk-text-h2-sm)/1.15 var(--mk-sans)" }}>
          What you get by joining during beta
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,280px),1fr))",
            gap: "40px 32px",
          }}
        >
          {BENEFITS.map(({ Icon, title, body }) => (
            <div
              key={title}
              className="mk-stack"
              style={{ gap: 12, paddingTop: 24, borderTop: "1px solid var(--mk-border)" }}
            >
              <Icon size={28} color="var(--mk-primary)" aria-hidden="true" />
              <h3 style={{ margin: "6px 0 0", font: "600 var(--mk-text-h3)/1.25 var(--mk-sans)" }}>{title}</h3>
              <p className="mk-body">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="mk-section mk-section--rule">
      <div
        className="mk-container"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,360px),1fr))",
          gap: "clamp(32px,5cqi,80px)",
          alignItems: "start",
        }}
      >
        <div className="mk-stack" style={{ gap: 20 }}>
          <div className="mk-label">Common Questions</div>
          <h2 className="mk-h2 mk-h2--sm">Common questions.</h2>
        </div>
        <Accordion items={FAQS} />
      </div>
    </section>

    <CtaBand
      title="Join free. Keep your founding rate while you stay subscribed."
      gap={28}
      ctaOffset={0}
      cta={
        <WaitlistCta variant="inverse" size="lg" style={{ padding: "0 26px" }}>
          Get Early Access
        </WaitlistCta>
      }
    />
  </MarketingLayout>
);

export default PricingPage;
